import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getListProductsQueryKey, useUpdateProduct } from "@workspace/api-client-react";
import { ArrowLeft, AlertTriangle, Loader2, Package2, Printer, Save } from "lucide-react";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

type VariantRow = {
  id: string;
  label: string;
  sku: string;
  price: number;
  stock: number;
  attribute1Label?: string;
  attribute1Value?: string;
  attribute2Label?: string;
  attribute2Value?: string;
};

type VariantProduct = {
  id: number;
  name: string;
  sku: string;
  price: number | string;
  stock: number;
  hasVariants?: boolean;
  variants?: string | null;
  categoryName?: string | null;
};

function parseVariants(raw?: string | null): VariantRow[] | null {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const ids = new Set<string>();
    return parsed.map(value => {
      if (!value || typeof value !== "object") throw new Error("Invalid variant");
      const row = value as Record<string, unknown>;
      const id = String(row.id ?? "").trim();
      const label = String(row.label ?? "").trim();
      const priceValue = row.price;
      const stockValue = row.stock;
      const price = typeof priceValue === "number" || (typeof priceValue === "string" && priceValue.trim() !== "")
        ? Number(priceValue)
        : Number.NaN;
      const stock = typeof stockValue === "number" || (typeof stockValue === "string" && stockValue.trim() !== "")
        ? Number(stockValue)
        : Number.NaN;
      if (!id || ids.has(id) || !label || !Number.isFinite(price) || price < 0 ||
        !Number.isFinite(stock) || stock < 0) {
        throw new Error("Invalid variant");
      }
      ids.add(id);
      return {
        id,
        label,
        sku: String(row.sku ?? ""),
        price,
        stock,
        ...(typeof row.attribute1Label === "string" ? { attribute1Label: row.attribute1Label } : {}),
        ...(typeof row.attribute1Value === "string" ? { attribute1Value: row.attribute1Value } : {}),
        ...(typeof row.attribute2Label === "string" ? { attribute2Label: row.attribute2Label } : {}),
        ...(typeof row.attribute2Value === "string" ? { attribute2Value: row.attribute2Value } : {}),
      };
    });
  } catch {
    return null;
  }
}

function splitLabel(label: string) {
  const parts = label.split(/\s*\/\s*/);
  if (parts.length >= 2 && parts[0].trim() && parts.slice(1).join("/").trim()) {
    return { color: parts[0].trim(), size: parts.slice(1).join("/").trim() };
  }
  return { color: "Other", size: label };
}

function VariantProductCard({ product, onPrintVariant }: {
  product: VariantProduct;
  onPrintVariant?: (product: VariantProduct, variant: VariantRow) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const mutation = useUpdateProduct();
  const original = useMemo(() => parseVariants(product.variants), [product.variants]);
  const [draft, setDraft] = useState<VariantRow[]>(() => original ?? []);
  const [saving, setSaving] = useState(false);

  const total = draft.reduce((sum, variant) => sum + variant.stock, 0);
  const stockMismatch = Math.abs(total - Number(product.stock)) > 0.0001;
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original],
  );
  const matrix = useMemo(() => {
    const byRow = new Map<string, Map<string, VariantRow[]>>();
    const rowValues = new Set<string>();
    const columnValues = new Set<string>();
    const metadataVariant = draft.find(variant =>
      variant.attribute1Label && variant.attribute1Value &&
      variant.attribute2Label && variant.attribute2Value,
    );
    const rowLabel = metadataVariant?.attribute1Label ?? "Color";
    const columnLabel = metadataVariant?.attribute2Label ?? "Size";
    for (const variant of draft) {
      const hasAttributes = !!(
        variant.attribute1Label && variant.attribute1Value &&
        variant.attribute2Label && variant.attribute2Value
      );
      const rowValue = hasAttributes ? variant.attribute1Value! : splitLabel(variant.label).color;
      const columnValue = hasAttributes ? variant.attribute2Value! : splitLabel(variant.label).size;
      rowValues.add(rowValue);
      columnValues.add(columnValue);
      const row = byRow.get(rowValue) ?? new Map<string, VariantRow[]>();
      const cell = row.get(columnValue) ?? [];
      cell.push(variant);
      row.set(columnValue, cell);
      byRow.set(rowValue, row);
    }
    return {
      rowLabel,
      columnLabel,
      rowValues: [...rowValues].sort((a, b) => a.localeCompare(b)),
      columnValues: [...columnValues].sort((a, b) => a.localeCompare(b)),
      byRow,
    };
  }, [draft]);

  const updateStock = (id: string, value: string) => {
    const stock = value === "" ? 0 : Number(value);
    if (!Number.isFinite(stock) || stock < 0) return;
    setDraft(current => current.map(variant => variant.id === id ? { ...variant, stock } : variant));
  };

  const save = () => {
    if (!dirty && !stockMismatch) return;
    if (stockMismatch && !window.confirm(
      `The variant counts add up to ${total}, while the product currently shows ${Number(product.stock)}. Saving will set total product stock to ${total}. Continue only if these counts have been checked.`,
    )) return;

    setSaving(true);
    mutation.mutate({
      id: product.id,
      data: {
        hasVariants: true,
        variants: JSON.stringify(draft),
        reconcileVariantStock: true,
      },
    }, {
      onSuccess: updatedProduct => {
        const savedVariants = parseVariants(updatedProduct.variants);
        if (savedVariants) setDraft(savedVariants);
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        const savedTotal = savedVariants?.reduce((sum, variant) => sum + variant.stock, 0) ?? total;
        toast({ title: "Variant stock saved", description: `${product.name} now has ${savedTotal} unit${savedTotal === 1 ? "" : "s"} across its variants.` });
      },
      onError: error => {
        toast({
          title: "Could not save variant stock",
          description: error instanceof Error ? error.message : "Refresh and try again.",
          variant: "destructive",
        });
      },
      onSettled: () => setSaving(false),
    });
  };

  if (original === null || original.length === 0) {
    return (
      <section className="rounded-xl border border-destructive/40 bg-card p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
          <div>
            <h2 className="font-semibold">{product.name}</h2>
            <p className="mt-1 text-sm text-destructive">
              {original === null
                ? "Variant data is invalid. Edit the product and correct its variant rows before changing stock."
                : "No variant rows are configured. Edit the product and add variants before changing stock."}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">{product.name}</h2>
            <Badge variant="secondary">{product.sku}</Badge>
            {product.categoryName && <span className="text-xs text-muted-foreground">{product.categoryName}</span>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {draft.length} variant{draft.length === 1 ? "" : "s"} · {total.toLocaleString()} units total
          </p>
        </div>
        <Button onClick={save} disabled={saving || (!dirty && !stockMismatch)} className="shrink-0 gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save counts
        </Button>
      </div>

      {stockMismatch && (
        <div className="flex items-start gap-2 border-b border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100 sm:px-5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Variant counts total {total.toLocaleString()}, but product stock is {Number(product.stock).toLocaleString()}.
            Past product-level stock changes may not identify a specific variant. Check each quantity before saving.
          </p>
        </div>
      )}

      {draft.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">No variants are configured for this product.</div>
      ) : (
        <div className="overflow-x-auto p-4 sm:p-5">
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr>
                <th className="border bg-muted/50 px-3 py-2 text-left font-medium">{matrix.rowLabel} / {matrix.columnLabel}</th>
                {matrix.columnValues.map(columnValue => (
                  <th key={columnValue} className="border bg-muted/50 px-3 py-2 text-left font-medium">{columnValue}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rowValues.map(rowValue => (
                <tr key={rowValue}>
                  <th className="border bg-muted/20 px-3 py-2 text-left font-medium">{rowValue}</th>
                  {matrix.columnValues.map(columnValue => {
                    const variants = matrix.byRow.get(rowValue)?.get(columnValue) ?? [];
                    return (
                      <td key={columnValue} className="border p-2 align-top">
                        {variants.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="space-y-2">
                            {variants.map(variant => (
                              <div key={variant.id} className="flex min-w-28 items-center gap-2">
                                <Input
                                  aria-label={`${product.name}, ${variant.label}, stock`}
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={variant.stock}
                                  onChange={event => updateStock(variant.id, event.target.value)}
                                  className="h-9 w-24"
                                />
                                {variants.length > 1 && (
                                  <span className="max-w-24 truncate text-xs text-muted-foreground" title={variant.sku || variant.id}>
                                    {variant.sku || variant.label}
                                  </span>
                                )}
                                {onPrintVariant && (
                                  <Button type="button" variant="ghost" size="icon"
                                    data-testid={`button-print-variant-${product.id}-${variant.id}`}
                                    aria-label={`Print label for ${product.name}, ${variant.label}`}
                                    title={`Print label for ${variant.label}`}
                                    onClick={() => onPrintVariant(product, variant)}
                                  >
                                    <Printer className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">
            Rows use Attribute 1 and columns use Attribute 2 when configured. Older variants without attribute metadata keep their existing Color/Size grouping.
          </p>
        </div>
      )}
    </section>
  );
}

export function VariantMatrix({ products, isLoading, onPrintVariant }: {
  products?: VariantProduct[];
  isLoading: boolean;
  onPrintVariant?: (product: VariantProduct, variant: VariantRow) => void;
}) {
  const variantProducts = (products ?? []).filter(product => product.hasVariants);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-2.5 text-primary"><Package2 className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Variant Stock Matrix</h1>
            <p className="mt-1 text-sm text-muted-foreground">Review and update stock by each product’s configured attributes.</p>
          </div>
        </div>
        <Button variant="outline" asChild className="gap-2 self-start">
          <Link href="/inventory"><ArrowLeft className="h-4 w-4" /> Back to products</Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground">Loading variant stock…</div>
      ) : variantProducts.length > 0 ? (
        <div className="space-y-4">
          {variantProducts.map(product => <VariantProductCard key={product.id} product={product} onPrintVariant={onPrintVariant} />)}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed bg-card px-6 py-12 text-center">
          <Package2 className="mx-auto h-9 w-9 text-muted-foreground" />
          <h2 className="mt-3 font-semibold">No variant products yet</h2>
          <p className="mt-1 text-sm text-muted-foreground">Add a product with variants to review stock by attribute.</p>
        </div>
      )}
    </div>
  );
}